// pack-pin-drift-detector.ts
//
// §18 pin/version drift (phase-08 Agent H, tests 27-28). Tuple tracked:
//   1. pack version + a digest of the mission workspace's own
//      `.claude/pack/.avatar-pack-manifest.json` (sync/update rewrites this
//      file — hashing its bytes catches ANY manifest change, not just a
//      version-string bump).
//   2. `claude --version` output — an injected runner, never a direct spawn
//      here (doctor/tests control the probe; auto-update on the operator's
//      machine is exactly the drift case #3 in the phase file's key
//      insights).
//   3. settings/profile digest already computed by
//      mission-initializer-compatibility.ts (P5) and persisted in
//      `<control_root>/compatibility.json` — reused, not recomputed, so
//      this module never duplicates computeSettingsProfileDigest's formula.
//
// A tuple mismatch against the last VERIFIED tuple (written at init, or at
// a later explicit re-verify) blocks new dispatch attempts until an operator
// runs `avatar autopilot doctor --reverify`, which captures a fresh tuple,
// persists it as newly-verified, and appends a journal receipt (wired in
// autopilot-doctor-ops-checks.ts).
import { createHash } from "node:crypto";
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists, readJson, readText } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export interface PinTuple {
  packVersion: string;
  packManifestDigest: string;
  claudeBinaryVersion: string;
  settingsProfileDigest: string;
  capturedAt: string;
}

export interface CaptureVerifiedTupleInput {
  missionWorkspace: string;
  controlRoot: string;
  now?: () => Date;
  /** Injected — never spawns `claude` directly here (test/doctor control it). */
  probeClaudeVersion: () => Promise<string>;
}

const MANIFEST_RELATIVE_PATH = join(".claude", "pack", ".avatar-pack-manifest.json");
const VERIFIED_TUPLE_FILE = "pack-pin-verified-tuple.json";

async function readPackManifestVersionAndDigest(
  missionWorkspace: string,
): Promise<{ version: string; digest: string }> {
  const manifestPath = join(missionWorkspace, MANIFEST_RELATIVE_PATH);
  if (!(await pathExists(manifestPath))) {
    return { version: "(unknown)", digest: "(no-manifest)" };
  }
  const raw = await readText(manifestPath);
  const digest = createHash("sha256").update(raw, "utf8").digest("hex");
  let version = "(unknown)";
  try {
    const parsed = JSON.parse(raw) as { version?: unknown };
    if (typeof parsed.version === "string") version = parsed.version;
  } catch {
    // Malformed manifest JSON — version stays "(unknown)", digest still
    // reflects the raw bytes so drift is still detectable.
  }
  return { version, digest };
}

async function readSettingsProfileDigest(controlRoot: string): Promise<string> {
  const compatPath = join(controlRoot, "compatibility.json");
  if (!(await pathExists(compatPath))) return "(no-compatibility-manifest)";
  try {
    const compat = await readJson<{ settings_digest?: unknown }>(compatPath);
    return typeof compat.settings_digest === "string" ? compat.settings_digest : "(unknown)";
  } catch {
    return "(unreadable)";
  }
}

/** Captures the CURRENT tuple — does not compare against anything, purely observational. */
export async function captureVerifiedTuple(input: CaptureVerifiedTupleInput): Promise<PinTuple> {
  const now = input.now ?? (() => new Date());
  const { version, digest } = await readPackManifestVersionAndDigest(input.missionWorkspace);
  const claudeBinaryVersion = await input.probeClaudeVersion();
  const settingsProfileDigest = await readSettingsProfileDigest(input.controlRoot);

  return {
    packVersion: version,
    packManifestDigest: digest,
    claudeBinaryVersion,
    settingsProfileDigest,
    capturedAt: now().toISOString(),
  };
}

function tuplePath(controlRoot: string): string {
  return join(controlRoot, VERIFIED_TUPLE_FILE);
}

/** Persists a tuple as the last VERIFIED one (called at init, and at re-verify). */
export async function writeVerifiedTuple(controlRoot: string, tuple: PinTuple): Promise<void> {
  await writeFileDurable(
    tuplePath(controlRoot),
    Buffer.from(`${JSON.stringify(tuple, null, 2)}\n`, "utf8"),
  );
}

/** Reads the last VERIFIED tuple — `undefined` when none exists yet (fresh mission, not drift). */
export async function readVerifiedTuple(controlRoot: string): Promise<PinTuple | undefined> {
  const path = tuplePath(controlRoot);
  if (!(await pathExists(path))) return undefined;
  try {
    return JSON.parse(await fs.readFile(path, "utf8")) as PinTuple;
  } catch {
    return undefined;
  }
}

export interface PinDriftDecision {
  drift: boolean;
  blocked: boolean;
  changedFields: string[];
}

const COMPARED_FIELDS: readonly (keyof Omit<PinTuple, "capturedAt">)[] = [
  "packVersion",
  "packManifestDigest",
  "claudeBinaryVersion",
  "settingsProfileDigest",
];

/**
 * Compares a freshly-captured tuple against the last VERIFIED one. No
 * verified tuple yet => not drift (first capture becomes the baseline via
 * `writeVerifiedTuple`, typically at init). Any compared field differing =>
 * drift => blocks new attempts until re-verify.
 */
export function detectPinDrift(
  current: PinTuple,
  verified: PinTuple | undefined,
): PinDriftDecision {
  if (verified === undefined) return { drift: false, blocked: false, changedFields: [] };

  const changedFields = COMPARED_FIELDS.filter((field) => current[field] !== verified[field]);
  const drift = changedFields.length > 0;
  return { drift, blocked: drift, changedFields };
}

export interface ReverifyReceipt {
  reverified_at: string;
  changed_fields: string[];
  previous_capture_at?: string;
  new_capture_at: string;
}

const REVERIFY_RECEIPTS_FILE = "pack-pin-reverify-receipts.jsonl";

/**
 * `doctor --reverify` entry point: captures a fresh tuple, persists it as
 * the new VERIFIED baseline, and appends a receipt line to a dedicated JSONL
 * (kept separate from `journal.jsonl`'s typed delta vocabulary — a
 * standalone append-only receipt log, same "ghi bền" durability primitive
 * via writeFileDurable, without requiring a new mission-state-reducer.ts
 * delta kind for what is purely an operational/compatibility record, not a
 * mission-state transition).
 */
export async function reverifyPinTuple(
  input: CaptureVerifiedTupleInput,
): Promise<{ tuple: PinTuple; receipt: ReverifyReceipt }> {
  const previous = await readVerifiedTuple(input.controlRoot);
  const tuple = await captureVerifiedTuple(input);
  await writeVerifiedTuple(input.controlRoot, tuple);

  const receipt: ReverifyReceipt = {
    reverified_at: tuple.capturedAt,
    changed_fields: previous ? detectPinDrift(tuple, previous).changedFields : [],
    previous_capture_at: previous?.capturedAt,
    new_capture_at: tuple.capturedAt,
  };

  const receiptsPath = join(input.controlRoot, REVERIFY_RECEIPTS_FILE);
  const existing = (await pathExists(receiptsPath)) ? await fs.readFile(receiptsPath, "utf8") : "";
  await writeFileDurable(
    receiptsPath,
    Buffer.from(`${existing}${JSON.stringify(receipt)}\n`, "utf8"),
  );

  return { tuple, receipt };
}
